<template>
  <div class="app-nav d-print-none">
    <!-- Mobile / tablet top bar -->
    <header ref="topbar" class="app-topbar d-lg-none">
      <button class="app-icon-btn" aria-label="Open menu" @click="open = true">
        <b-icon icon="list" font-scale="1.35"></b-icon>
      </button>
      <div class="app-topbar-title">{{ currentLabel }}</div>
      <span class="app-avatar">{{ userInitial }}</span>
    </header>

    <transition name="app-fade">
      <div v-if="open" class="app-backdrop d-lg-none" @click="open = false"></div>
    </transition>

    <!-- Sidebar (fixed on desktop, drawer on mobile) -->
    <aside :class="['app-sidebar', { open }]">
      <router-link to="/" class="app-brand">
        <span class="app-brand-logo"><img src="../assets/aslogo.png" alt="Logo"></span>
        <span class="app-brand-text">
          <strong>Airsuspension</strong>
          <small>Administrator</small>
        </span>
      </router-link>

      <nav class="app-menu">
        <div v-for="section in menu" :key="section.title" :class="['app-menu-section', `tone-${section.tone}`, sectionClass(section)]">
          <button class="app-menu-title"
                  :aria-expanded="String(!collapsed.includes(section.title))"
                  @click="toggleSection(section.title)">
            <span>{{ section.title }}</span>
            <span v-if="collapsed.includes(section.title) && hasActive(section)" class="app-menu-dot"></span>
            <b-icon icon="chevron-down"
                    :class="['app-menu-chevron', { collapsed: collapsed.includes(section.title) }]"></b-icon>
          </button>
          <div v-show="!collapsed.includes(section.title)">
            <router-link v-for="item in section.items"
                         :key="item.to"
                         :to="item.to"
                         :class="['app-menu-item', itemClass(item)]">
              <b-icon :icon="item.icon" class="app-menu-icon"></b-icon>
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </nav>

      <div class="app-user">
        <span class="app-avatar">{{ userInitial }}</span>
        <span class="app-user-name">{{ user.username || 'Please Log In' }}</span>
        <button class="app-icon-btn" title="Sign out" aria-label="Sign out" @click="logOut">
          <b-icon icon="box-arrow-right"></b-icon>
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import axios from 'axios';
import fixed from '@/fixed';
import { menuFor, itemClass, sectionClass } from '@/navigation';

// Collapsed sidebar sections are a per-browser preference; storage can be
// unavailable (private mode), in which case everything just starts expanded.
const COLLAPSED_KEY = 'navCollapsedSections';

function readCollapsed() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLLAPSED_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch (e) {
    return [];
  }
}

export default {
  props: ['user'],
  data: () => ({
    open: false,
    collapsed: readCollapsed(),
  }),
  computed: {
    menu() {
      return menuFor(this.user);
    },
    currentLabel() {
      const items = this.menu.flatMap((s) => s.items);
      const match = items.find((i) => this.$route.path.startsWith(i.to));
      return match ? match.label : 'Airsuspension';
    },
    userInitial() {
      return (this.user && this.user.username ? this.user.username : '?')
        .charAt(0)
        .toUpperCase();
    },
  },
  watch: {
    $route() {
      this.open = false;
    },
  },
  mounted() {
    // Publish the live top bar height so sticky page content can sit below it.
    // It is display:none on desktop, which reports 0 — exactly what we want.
    this.resizeObserver = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--navbar-height', `${entry.target.offsetHeight}px`);
    });
    this.resizeObserver.observe(this.$refs.topbar);
  },
  beforeDestroy() {
    this.resizeObserver.disconnect();
    document.documentElement.style.removeProperty('--navbar-height');
  },
  methods: {
    itemClass,
    sectionClass,
    hasActive(section) {
      return section.items.some((i) => this.$route.path.startsWith(i.to));
    },
    toggleSection(title) {
      this.collapsed = this.collapsed.includes(title)
        ? this.collapsed.filter((t) => t !== title)
        : [...this.collapsed, title];
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(this.collapsed));
      } catch (e) {
        // Storage unavailable: the choice just won't survive a reload.
      }
    },
    async logOut() {
      try {
        await axios.get(`${fixed.API_URL}/log-out`, { withCredentials: true });
        localStorage.removeItem('user');
        window.location.reload();
      } catch (err) {
        this.$toast.error(err?.message || 'log out error');
      }
    },
  },
};
</script>

<style>
/* ---------- Top bar (below lg) ---------- */
.app-topbar {
  position: sticky;
  top: 0;
  z-index: 1030;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 56px;
  padding: env(safe-area-inset-top, 0px) 12px 0;
  background: var(--chrome);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid var(--border);
}

.app-topbar-title {
  flex: 1;
  font-weight: 650;
  font-size: 1rem;
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-muted);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.app-icon-btn:hover {
  background: var(--hover);
  color: var(--text);
}

.app-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), #8b5cf6);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
}

/* ---------- Sidebar ---------- */
.app-sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1030;
  width: var(--sidebar-w);
  display: flex;
  flex-direction: column;
  background: var(--chrome);
  backdrop-filter: saturate(180%) blur(24px);
  -webkit-backdrop-filter: saturate(180%) blur(24px);
  border-right: 1px solid var(--border);
}

.app-brand {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 18px 16px 14px;
  color: var(--text) !important;
  text-decoration: none !important;
}

.app-brand-logo {
  display: inline-flex;
  padding: 6px;
  border-radius: 10px;
  background: var(--surface-3);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.app-brand-logo img {
  width: 26px;
  height: auto;
}

.app-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.app-brand-text strong {
  font-size: 0.9rem;
  font-weight: 700;
}

.app-brand-text small {
  font-size: 0.72rem;
  color: var(--text-faint);
  font-weight: 500;
}

.app-menu {
  flex: 1;
  overflow-y: auto;
  padding: 4px 10px 12px;
}

.app-menu-section + .app-menu-section {
  margin-top: 14px;
}

.app-menu-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 4px 10px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: left;
  color: rgba(var(--tone-rgb), 0.8);
  cursor: pointer;
  transition: color 0.12s ease;
}

.app-menu-title:hover {
  color: var(--tone);
}

.app-menu-chevron {
  margin-left: auto;
  font-size: 0.75rem;
  opacity: 0;
  transition: transform 0.2s ease, opacity 0.12s ease;
}

.app-menu-title:hover .app-menu-chevron,
.app-menu-title:focus-visible .app-menu-chevron,
.app-menu-chevron.collapsed {
  opacity: 1;
}

.app-menu-chevron.collapsed {
  transform: rotate(-90deg);
}

/* Marks a collapsed section that holds the page you're on */
.app-menu-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
}

.app-menu-item {
  align-items: center;
  gap: 0.65rem;
  padding: 6px 10px;
  margin: 1px 0;
  border-radius: var(--radius-sm);
  color: var(--text-muted) !important;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none !important;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.app-menu-item:hover {
  background: var(--hover);
  color: var(--text) !important;
}

.app-menu-item.router-link-active {
  background: var(--accent-soft);
  color: var(--text) !important;
}

.app-menu-icon {
  flex-shrink: 0;
  font-size: 1rem;
  opacity: 0.85;
}

.app-menu-item.router-link-active .app-menu-icon {
  color: var(--accent);
  opacity: 1;
}

.app-user {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 12px 14px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--border);
}

.app-user-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- Drawer behaviour below lg ---------- */
@media (max-width: 991.98px) {
  .app-sidebar {
    z-index: 1045;
    width: min(300px, 84vw);
    background: var(--surface);
    box-shadow: var(--shadow-lg);
    transform: translateX(-105%);
    transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1);
  }

  .app-sidebar.open {
    transform: none;
  }

  .app-menu-item {
    padding: 10px 12px;
    font-size: 0.95rem;
  }
}

.app-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1040;
  background: rgba(0, 0, 0, 0.5);
}

.app-fade-enter-active,
.app-fade-leave-active {
  transition: opacity 0.2s ease;
}

.app-fade-enter,
.app-fade-leave-to {
  opacity: 0;
}
</style>
